import { Bot, FileText, Headset, LayoutDashboard, Settings } from "lucide-react";
import { ApiUrl } from "../config/apiConfig";
import { postData, putData } from "../services/Axios/Axios";
import messageService from "../services/message/messageService";

enum CourseShortName {
    FreightOriginator = "Originator",
    TruckDispatcher = "Dispatcher",
    FreightBroker = "Freight Broker",
    EnterpriseBroker = "Enterprise Broker",
}

function getShortCourseName(courseName: string): string {
    switch (courseName) {
        case "Freight Originator Business Full Training":
            return CourseShortName.FreightOriginator;
        case "Truck Dispatcher Training":
            return CourseShortName.TruckDispatcher;
        case "Freight Broker Training":
            return CourseShortName.FreightBroker;
        case "Enterprise Broker Training":
            return CourseShortName.EnterpriseBroker;
        default:
            return courseName;
    }
}

const courseColors: Record<string, string> = {
    "Freight Originator Business Full Training": "bg-[#FF9800]",
    "Truck Dispatcher Training": "bg-[#8E84FF]",
    "Freight Broker Training": "bg-pink-500",
    "Enterprise Broker Training": "bg-green-500",
};

function getCourseColor(courseName: string): string {
    return courseColors[courseName] || "bg-gray-400";
}

interface Lead {
    _id: string;
    updatedAt: string | Date;
    [key: string]: any; // in case loads have extra fields
}

function getTodaysLeads(loads: Lead[]): Lead[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // start of today

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1); // start of tomorrow

    return loads
        .filter((load) => {
            const updated = new Date(load.updatedAt);
            return updated >= today && updated < tomorrow;
        })
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

function deleteLead(leadId: string, onDeleteSuccess: () => void) {
    const messageServices = messageService();
    messageServices.confirm(`delete Lead`, "").then(async (res) => {
        if (res.value) {
            await putData(`/v1/admissions/updateEnrollById/${leadId}`, {
                isDeleted: true,
            });

            onDeleteSuccess();
        }
    });
}

function restoreLead(leadId: string, onRestoreSuccess: () => void) {
    const messageServices = messageService();
    messageServices.confirm(`Restore Lead`, "").then(async (res) => {
        if (res.value) {
            await putData(`/v1/admissions/updateEnrollById/${leadId}`, {
                isDeleted: false,
            });

            onRestoreSuccess();
        }
    });
}

// **************** Student Functions *******************
function convertLeadToStudentPayload(lead: any) {
    return {
        firstName: lead.firstName || "",
        lastName: lead.lastName || "",
        city: lead.city || "",
        state: lead.state || "",
        country: lead.country || "",

        phone: lead.phone || "",
        email: lead.email || "",

        phoneCode: {
            value: lead.phoneCode?.value || "",
            label: lead.phoneCode?.label || "",
            country: lead.phoneCode?.country || "",
        },

        language: Array.isArray(lead.language) ? lead.language[0] : lead.language || "",

        course: Array.isArray(lead.training) ? lead.training[0] : lead.training || "",

        courseAmt: lead.courseAmt ?? 0,
        dueAmt: lead.dueAmt ?? 0,
        paidFee: lead.paidFee ?? 0,
        discountApplied: lead.discountApplied ?? 0,

        paymentMethod: lead.paymentMethod || "None",

        status: "new-entry",

        classType: lead.classType || "inperson",

        admissionDate: new Date(),

        admissionDoneBy: lead.admissionBy || "",

        notes: "",

        isDeleted: lead.isDeleted ?? false,
    };
}

async function createStudentByLead(lead: any) {
    try {
        await postData(ApiUrl.createStudent, convertLeadToStudentPayload(lead));
    } catch (error) {
        console.error(error);
    }
}

export type PermissionItem = {
    label: string;
    icon: any; // or LucideIcon for strict typing
};

function getDefaultPermissions(pageName: string): PermissionItem[] {
    switch (pageName) {
        case "Main":
            return [
                { label: "Dashboard", icon: LayoutDashboard },
                { label: "Live Support", icon: Headset },
                { label: "My Assistant", icon: Bot },
                { label: "Settings", icon: Settings },
            ];

        case "IFTA":
            return [
                { label: "Dashboard", icon: LayoutDashboard },
                { label: "IFTA", icon: FileText },
                { label: "Live Support", icon: Headset },
                { label: "Settings", icon: Settings },
            ];

        default:
            return [
                { label: "Dashboard", icon: LayoutDashboard },
                { label: "Live Support", icon: Headset },
                { label: "My Assistant", icon: Bot },
                { label: "Settings", icon: Settings },
            ];
    }
}

export {
    getShortCourseName,
    getCourseColor,
    getTodaysLeads,
    deleteLead,
    restoreLead,
    createStudentByLead,
    getDefaultPermissions
};
